import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Container, Card, Form, Button, Spinner, Alert, Table, Modal } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { protectedResources, scopes } from "../authConfig";
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import EntryUserEditForm from '../components/EntryUserEditForm';

const EntryUserRegistration = () => {
    const { entryId } = useParams();
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [entryDetails, setEntryDetails] = useState(null);
    const [userEntries, setUserEntries] = useState([]);
    const [companyId, setCompanyId] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [users, setUsers] = useState([createDefaultUser()]);
    const [seasonDetails, setSeasonDetails] = useState(null);
    const [themes, setThemes] = useState([]); // Added for theme selection
    const [loadingThemes, setLoadingThemes] = useState(false); // Added for theme selection loading state
    
    // New state for edit mode
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    // Add a computed value to check if entry count limit is reached
    const isEntryLimitReached = () => {
        if (!entryDetails || !entryDetails.entryCount) return false;
        const maxAllowed = parseInt(entryDetails.entryCount);
        const currentTotal = userEntries.length + users.length;
        return currentTotal >= maxAllowed;
    };

    // Add a function to check if we're within the acceptance period
    const isWithinAcceptancePeriod = () => {
        if (!seasonDetails) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day
        
        const startDate = seasonDetails.acceptanceStartDate ? new Date(seasonDetails.acceptanceStartDate) : null;
        const endDate = seasonDetails.acceptanceEndDate ? new Date(seasonDetails.acceptanceEndDate) : null;
        
        if (!startDate || !endDate) return false;
        
        // Set dates to beginning of day for fair comparison
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999); // End of day for end date
        
        return today >= startDate && today <= endDate;
    };
    
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    // Function to generate a random password
    function generateRandomPassword() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    }

    // Function to create a default user object
    function createDefaultUser() {
        return { 
            displayName: '', 
            surname: '', 
            givenName: '', 
            loginName: '',
            password: generateRandomPassword(),
            accountEnabled: true,  // Always true, no longer configurable
            themePreference1: '',  // First choice theme
            themePreference2: '',  // Second choice theme
            themePreference3: '',  // Third choice theme
            themePreference4: ''   // Fourth choice theme
        };
    }

    // Fetch themes related to season
    const fetchThemesBySeasonId = useCallback(async (seasonId) => {
        if (!seasonId) return;
        
        setLoadingThemes(true);
        
        try {
            const response = await execute(
                'GET',
                `${protectedResources.getMasterdatas.endpoint}/theme?seasonId=${seasonId}`
            );
            
            if (response && Array.isArray(response)) {
                setThemes(response);
            } else {
                setThemes([]);
                console.warn("テーマ情報の取得に失敗しました。");
            }
        } catch (error) {
            console.error("Error fetching themes:", error);
            setThemes([]);
        } finally {
            setLoadingThemes(false);
        }    }, [execute]);
    
    // Validate users input
    function validateUsers(usersToValidate) {
        return usersToValidate.filter(user => 
            !user.surname.trim() || 
            !user.givenName.trim() || 
            !user.loginName.trim()
        );
    }

    // Fetch entry details and user entries
    const fetchData = useCallback(async () => {
        if (!entryId) {
            setError("エントリーIDが指定されていません。");
            setLoading(false);
            return;
        }

        try {
            // Fetch entry details first
            const entryDetailsResponse = await execute(
                'GET',
                `${protectedResources.getEntry.endpoint}/${entryId}`
            );
            
            if (entryDetailsResponse !== null && entryDetailsResponse !== undefined) {
                setEntryDetails(entryDetailsResponse);
                
                // Now fetch season details to get acceptance period
                if (entryDetailsResponse.seasonId) {
                    const seasonResponse = await execute(
                        'GET',
                        `${protectedResources.getMasterData.endpoint}/season?id=${entryDetailsResponse.seasonId}`
                    );
                    
                    if (seasonResponse) {
                        setSeasonDetails(seasonResponse);
                    }
                }
            }

            // Then fetch user entries
            const userEntriesResponse = await execute(
                'GET',
                `${protectedResources.getUserEntries.endpoint}?entryId=${entryId}`
            );
            
            if (userEntriesResponse) {
                // Extract user entries from the response
                setError(null);
                if (Array.isArray(userEntriesResponse.userEntries)) {
                    setUserEntries(userEntriesResponse.userEntries);
                } else if (userEntriesResponse.userEntries === undefined) {
                    setUserEntries([]);
                } else {
                    setUserEntries([]);
                    console.warn("ユーザーエントリーのレスポンス形式が予期せぬものです。", userEntriesResponse);
                }
            } else {
                setUserEntries([]);
                console.warn("ユーザーエントリー情報の取得に失敗しました。");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("情報の取得中にエラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [entryId, execute]);

    // Initialize component with user and entry data
    useEffect(() => {
        const activeAccount = instance?.getActiveAccount();
        
        // Check permission type - only allow HR managers (type 3)
        if (activeAccount?.idTokenClaims?.extension_PermissionType !== 3) {
            navigate('/error');
            return;
        }
        // Set company information from user's token claims
        const userCompanyId = activeAccount?.idTokenClaims?.extension_CompanyId;
        if (userCompanyId) {
            setCompanyId(userCompanyId);
            
            // Get company name if available in claims
            const userCompanyName = activeAccount?.idTokenClaims?.extension_CompanyName;
            if (userCompanyName) {
                setCompanyName(userCompanyName);
            }
        } else {
            setError("ユーザーに関連付けられた企業情報が見つかりません。");
            setLoading(false);
            return;
        }

        fetchData();
    }, [entryId, instance, navigate, execute, fetchData]);    // Call fetchData when component mounts
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch themes when seasonDetails are available
    useEffect(() => {
        if (seasonDetails?.id) {
            fetchThemesBySeasonId(seasonDetails.id);
        }
    }, [seasonDetails, fetchThemesBySeasonId]);

    // Handle adding a new user form
    function handleAddUser() {
        if (isEntryLimitReached() || !isWithinAcceptancePeriod()) return;
        setUsers([...users, createDefaultUser()]);
    }

    // Handle removing a user form
    const handleRemoveUser = (index) => {
        const newUsers = [...users];
        newUsers.splice(index, 1);
        setUsers(newUsers);
    };    // Handle input change for a specific user
    const handleInputChange = (index, field, value) => {
        const newUsers = [...users];
        newUsers[index][field] = value;
        
        // If surname or givenName is updated, update displayName as well
        if (field === 'surname' || field === 'givenName') {
            const surname = field === 'surname' ? value : newUsers[index].surname;
            const givenName = field === 'givenName' ? value : newUsers[index].givenName;
            newUsers[index].displayName = `${surname} ${givenName}`.trim();
        }
        
        // No longer need to handle accountEnabled changes as it's not configurable
        
        setUsers(newUsers);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        
        // Prevent submission if outside acceptance period
        if (!isWithinAcceptancePeriod()) {
            setError("エントリー受付期間外のため、ユーザー登録ができません。");
            return;
        }
        
        setSaving(true);

        try {
            // Basic validation
            const invalidUsers = validateUsers(users);

            if (invalidUsers.length > 0) {
                setError("全てのユーザーの姓名とメールアドレスを入力してください。");
                setSaving(false);
                return;
            }

            // Format request payload
            const payload = {
                companyId: companyId,
                entryId: entryId,
                users: users
            };

            // Send API request
            const response = await execute(
                'POST',
                protectedResources.createBulkUsers.endpoint,
                payload
            );

            if (response && response.success) {
                setSuccess(`${users.length}人のユーザーが正常に登録されました。`);
                // Reset form with one empty user
                setUsers([createDefaultUser()]);
            } else {
                setError(response?.message || "ユーザー登録に失敗しました。");
            }
        } catch (error) {
            console.error("Error registering users:", error);
            setError("ユーザー登録中にエラーが発生しました。");
        } finally {
            setSaving(false);
        }
    };

    // Handle opening edit modal
    const handleOpenEditModal = (user) => {
        // Prevent editing if outside acceptance period
        if (!isWithinAcceptancePeriod()) {
            setError("エントリー受付期間外のため、ユーザー情報の編集ができません。");
            return;
        }
        
        setEditingUser({
            id: user.id,
            userInfoId: user.userInfo?.id,
            displayName: user.userInfo?.displayName || '',
            surname: user.userInfo?.surname || '',
            givenName: user.userInfo?.givenName || '',
            loginName: user.userInfo?.identities?.[0]?.issuerAssignedId || 
                      user.userInfo?.mailNickname || '',
            accountEnabled: user.userInfo?.accountEnabled || false
        });
        setShowEditModal(true);
    };

    // Handle edit success
    const handleEditSuccess = (message) => {
        setSuccess(message);
        refreshUserEntries();
    };

    // Handle edit error
    const handleEditError = (message) => {
        setError(message);
    };

    // Refresh user entries data after edit
    const refreshUserEntries = async () => {
        try {
            const userEntriesResponse = await execute(
                'GET',
                `${protectedResources.getUserEntries.endpoint}?entryId=${entryId}`
            );
            
            if (userEntriesResponse && Array.isArray(userEntriesResponse.userEntries)) {
                setUserEntries(userEntriesResponse.userEntries);
            }
        } catch (error) {
            console.error("Error refreshing user entries:", error);
        }
    };

    // Handle delete user confirmation
    const handleOpenDeleteModal = (userId, userInfoId) => {
        // Prevent deletion if outside acceptance period
        if (!isWithinAcceptancePeriod()) {
            setError("エントリー受付期間外のため、ユーザーの削除ができません。");
            return;
        }
        
        setSelectedUserId({ userId, userInfoId });
        setShowDeleteModal(true);
    };

    // Handle delete user
    const handleDeleteUser = async () => {
        if (!selectedUserId) return;
        
        // Double-check we're within acceptance period
        if (!isWithinAcceptancePeriod()) {
            setError("エントリー受付期間外のため、ユーザーの削除ができません。");
            setShowDeleteModal(false);
            return;
        }
        
        setDeleting(true);
        setError(null);
        
        try {
            // Call API to delete user
            const response = await execute(
                'DELETE',
                `${protectedResources.deleteUser.endpoint}/${selectedUserId.userInfoId}`
            );

            if (response && response.success) {
                setSuccess("ユーザーが削除されました。");
                setShowDeleteModal(false);
                
                // Update userEntries state by removing the deleted user
                setUserEntries(prev => prev.filter(entry => entry.id !== selectedUserId.userId));
            } else {
                setError(response?.message || "ユーザーの削除に失敗しました。");
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            setError("ユーザーの削除中にエラーが発生しました。");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Container className="py-5">
            {/* Edit User Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>参加者情報編集</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingUser && (
                        <EntryUserEditForm 
                            user={editingUser} 
                            onDone={() => setShowEditModal(false)}
                            onSuccess={handleEditSuccess}
                            onError={handleEditError}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Delete User Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>参加者削除の確認</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p>このユーザーを削除してもよろしいですか？</p>
                    <p>この操作は取り消せません。</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        キャンセル
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteUser}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                削除中...
                            </>
                        ) : (
                            <>削除</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="fw-bold mb-0">参加者登録</h1>
                            {companyName && <p className="text-muted mb-0">会社名: {companyName}</p>}
                            {entryDetails && (
                                <p className="text-muted mb-0">
                                    期名: {entryDetails.seasonName || '-'} | 
                                    エントリー枠: {entryDetails.approvedEntryCount || 0}名
                                </p>
                            )}
                            {seasonDetails && (
                                <p className="text-muted mb-0">
                                    エントリー期間: {seasonDetails?.acceptanceStartDate || '-'} 〜 {seasonDetails?.acceptanceEndDate || '-'}
                                </p>
                            )}
                        </div>                        <Button
                            variant="outline-secondary"
                            className="d-flex align-items-center gap-2"
                            onClick={() => navigate(-1)}
                        >
                            <FaArrowLeft /> 戻る
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2">データ読み込み中...</p>
                </div>
            ) : (
                <>
                    {/* Show acceptance period warning if outside of period */}
                    {!isWithinAcceptancePeriod() && (
                        <Alert variant="warning" className="mb-4">
                            <h5 className="mb-2">エントリー受付期間外です</h5>
                            <p className="mb-0">
                                現在はエントリー受付期間外（{seasonDetails?.acceptanceStartDate || '-'} 〜 {seasonDetails?.acceptanceEndDate || '-'}）のため、
                                ユーザーの登録・編集・削除はできません。
                            </p>
                        </Alert>
                    )}

                    {/* 既存のユーザーエントリー表示セクション */}
                    {userEntries.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-light py-3">
                                <h5 className="mb-0">登録済み参加者</h5>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{width: "40px"}}>#</th>
                                            <th>氏名</th>
                                            <th>メールアドレス</th>
                                            <th>アカウント状態</th>
                                            <th>登録日</th>
                                            <th style={{width: "120px"}}>操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userEntries.map((entry, index) => (
                                            <tr key={entry.id}>
                                                <td>{index + 1}</td>
                                                <td>{entry.userInfo?.displayName || '未設定'}</td>
                                                <td>
                                                    {entry.userInfo?.identities?.[0]?.issuerAssignedId || 
                                                     entry.userInfo?.mailNickname || '未設定'}
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${entry.userInfo?.accountEnabled ? 'success' : 'danger'}`}>
                                                        {entry.userInfo?.accountEnabled ? '有効' : '無効'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {entry.createdAt 
                                                        ? new Date(entry.createdAt).toLocaleDateString('ja-JP')
                                                        : '未設定'
                                                    }
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={() => handleOpenEditModal(entry)}
                                                            title="編集"
                                                            disabled={!isWithinAcceptancePeriod()}
                                                        >
                                                            <FaEdit />
                                                        </Button>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            size="sm"
                                                            onClick={() => handleOpenDeleteModal(entry.id, entry.userInfo?.id)}
                                                            title="削除"
                                                            disabled={!isWithinAcceptancePeriod()}
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {userEntries.length > 0 && entryDetails?.entryCount && 
                     userEntries.length >= parseInt(entryDetails.entryCount) ? (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body className="text-center py-5">
                                <Alert variant="warning" className="mb-0">
                                    <h5 className="mb-2">登録上限に達しています</h5>
                                    <p className="mb-0">
                                        エントリー枠（{entryDetails.entryCount}名）の上限に達しているため、
                                        新たに参加者を登録することはできません。
                                    </p>
                                </Alert>
                            </Card.Body>
                        </Card>
                    ) : (
                        <>
                            <Form onSubmit={handleSubmit}>
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-light py-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">新規参加者登録</h5>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    type="button"
                                                    variant={isEntryLimitReached() || !isWithinAcceptancePeriod() ? "outline-secondary" : "outline-primary"}
                                                    onClick={handleAddUser}
                                                    className="d-flex align-items-center gap-2"
                                                    disabled={isEntryLimitReached() || !isWithinAcceptancePeriod()}
                                                    title={
                                                        !isWithinAcceptancePeriod() ? "エントリー受付期間外です" :
                                                        isEntryLimitReached() ? "エントリー上限に達しました" : 
                                                        "参加者を追加する"
                                                    }
                                                >
                                                    <FaPlus /> {
                                                        !isWithinAcceptancePeriod() ? "期間外" :
                                                        isEntryLimitReached() ? "上限到達" : 
                                                        "参加者追加"
                                                    }
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    disabled={saving || users.length === 0 || !isWithinAcceptancePeriod()}
                                                    className="d-flex align-items-center gap-2"
                                                    title={!isWithinAcceptancePeriod() ? "エントリー受付期間外です" : "保存する"}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <Spinner size="sm" animation="border" /> 保存中...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaSave /> 保存
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Header>

                                    <Card.Body>
                                        {error && (
                                            <Alert variant="danger" className="mb-4">
                                                {error}
                                            </Alert>
                                        )}
                                        
                                        {success && (
                                            <Alert variant="success" className="mb-4">
                                                {success}
                                            </Alert>
                                        )}

                                        {entryDetails?.entryCount && parseInt(entryDetails.entryCount) > 0 && (
                                            <Alert variant={isEntryLimitReached() ? "warning" : "info"} className="mb-4">
                                                現在のエントリー枠は <strong>{entryDetails.entryCount}名</strong> です。
                                                {userEntries.length > 0 && (
                                                    <>
                                                        すでに <strong>{userEntries.length}名</strong> が登録済みです。
                                                        {isEntryLimitReached() ? (
                                                            <strong> エントリー上限に達しました。</strong>
                                                        ) : (
                                                            <> 残り <strong>{Math.max(0, parseInt(entryDetails.entryCount) - userEntries.length)}名</strong> 登録できます。</>
                                                        )}
                                                    </>
                                                )}
                                                {userEntries.length === 0 && (
                                                    <>
                                                        {isEntryLimitReached() ? (
                                                            <strong> エントリー上限に達しました。</strong>
                                                        ) : (
                                                            <> 残り <strong>{Math.max(0, parseInt(entryDetails.entryCount))}名</strong> 登録できます。</>
                                                        )}
                                                    </>
                                                )}
                                            </Alert>
                                        )}

                                        <Table responsive className="mb-4">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{width: "40px"}}>#</th>
                                                    <th>姓 <span className="text-danger">*</span></th>
                                                    <th>名 <span className="text-danger">*</span></th>
                                                    <th>メールアドレス <span className="text-danger">*</span></th>
                                                    {/* テーマ希望選択のカラム追加 */}
                                                    <th>第一希望</th>
                                                    <th>第二希望</th>
                                                    <th>第三希望</th>
                                                    <th>第四希望</th>
                                                    <th style={{width: "80px"}}>操作</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map((user, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <Form.Control
                                                                type="text"
                                                                size="sm"
                                                                value={user.surname}
                                                                onChange={(e) => handleInputChange(index, 'surname', e.target.value)}
                                                                placeholder="姓"
                                                                required
                                                                disabled={!isWithinAcceptancePeriod()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="text"
                                                                size="sm"
                                                                value={user.givenName}
                                                                onChange={(e) => handleInputChange(index, 'givenName', e.target.value)}
                                                                placeholder="名"
                                                                required
                                                                disabled={!isWithinAcceptancePeriod()}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="email"
                                                                size="sm"
                                                                value={user.loginName}
                                                                onChange={(e) => handleInputChange(index, 'loginName', e.target.value)}
                                                                placeholder="メールアドレス"
                                                                required
                                                                disabled={!isWithinAcceptancePeriod()}
                                                            />
                                                        </td>
                                                        {/* テーマ選択用のドロップダウン - 第一希望 */}
                                                        <td>
                                                            <Form.Select
                                                                size="sm"
                                                                value={user.themePreference1}
                                                                onChange={(e) => handleInputChange(index, 'themePreference1', e.target.value)}
                                                                disabled={!isWithinAcceptancePeriod() || loadingThemes}
                                                            >
                                                                <option value="">選択してください</option>
                                                                {themes.map(theme => (
                                                                    <option key={theme.id} value={theme.id}>
                                                                        {theme.clientName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        {/* テーマ選択用のドロップダウン - 第二希望 */}
                                                        <td>
                                                            <Form.Select
                                                                size="sm"
                                                                value={user.themePreference2}
                                                                onChange={(e) => handleInputChange(index, 'themePreference2', e.target.value)}
                                                                disabled={!isWithinAcceptancePeriod() || loadingThemes}
                                                            >
                                                                <option value="">選択してください</option>
                                                                {themes.map(theme => (
                                                                    <option 
                                                                        key={theme.id} 
                                                                        value={theme.id}
                                                                        disabled={theme.id === user.themePreference1}
                                                                    >
                                                                        {theme.clientName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        {/* テーマ選択用のドロップダウン - 第三希望 */}
                                                        <td>
                                                            <Form.Select
                                                                size="sm"
                                                                value={user.themePreference3}
                                                                onChange={(e) => handleInputChange(index, 'themePreference3', e.target.value)}
                                                                disabled={!isWithinAcceptancePeriod() || loadingThemes}
                                                            >
                                                                <option value="">選択してください</option>
                                                                {themes.map(theme => (
                                                                    <option 
                                                                        key={theme.id} 
                                                                        value={theme.id}
                                                                        disabled={theme.id === user.themePreference1 || theme.id === user.themePreference2}
                                                                    >
                                                                        {theme.clientName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        {/* テーマ選択用のドロップダウン - 第四希望 */}
                                                        <td>
                                                            <Form.Select
                                                                size="sm"
                                                                value={user.themePreference4}
                                                                onChange={(e) => handleInputChange(index, 'themePreference4', e.target.value)}
                                                                disabled={!isWithinAcceptancePeriod() || loadingThemes}
                                                            >
                                                                <option value="">選択してください</option>
                                                                {themes.map(theme => (
                                                                    <option 
                                                                        key={theme.id} 
                                                                        value={theme.id}
                                                                        disabled={
                                                                            theme.id === user.themePreference1 || 
                                                                            theme.id === user.themePreference2 || 
                                                                            theme.id === user.themePreference3
                                                                        }
                                                                    >
                                                                        {theme.clientName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        <td>
                                                            {users.length > 1 && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveUser(index)}
                                                                    disabled={!isWithinAcceptancePeriod()}
                                                                >
                                                                    <FaTrash />
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted">
                                                <span className="text-danger">*</span> は必須項目です
                                            </small>
                                            <small className="text-muted">
                                                登録人数: {users.length}名
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-light py-3">
                                        <h5 className="mb-0">注意事項</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <ul className="mb-0">
                                            <li>各ユーザーには自動生成されたパスワードが設定されます。</li>
                                            <li>登録後、各ユーザーにログイン情報が通知されます。</li>
                                            <li>登録されたすべてのユーザーは自動的にアカウントが有効化されます。</li> {/* Updated this note */}
                                            <li>すべての参加者の権限は「参加者」に設定されます。</li>
                                            <li>会社情報は自動的に現在ログインしている会社に設定されます。</li>
                                            <li>エントリーの登録・編集・削除は受付期間（{seasonDetails?.acceptanceStartDate || '-'} 〜 {seasonDetails?.acceptanceEndDate || '-'}）内のみ可能です。</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Form>
                        </>
                    )}
                    
                    {success && (
                        <Alert
                            variant="success"
                            className="mb-4"
                            onClose={() => setSuccess(null)}
                            dismissible
                        >
                            {success}
                        </Alert>
                    )}
                </>
            )}
        </Container>
    );
};

export default EntryUserRegistration;
